import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Star, MapPin, ArrowLeft, Send, CheckCircle, ChevronRight, Users
} from "lucide-react";
import tourData from "../assets/data/tours";

/**
 * Inline rating calculator — eliminates external dependency that could
 * 404 during hosting. If your project already has this util, you can
 * swap this out, but this guarantees zero import failures.
 */
const calculateAvgRating = (reviews) => {
  if (!reviews || reviews.length === 0) return { totalRating: 0, avgRating: 0 };
  const total = reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0);
  return {
    totalRating: reviews.length,
    avgRating: Math.round((total / reviews.length) * 10) / 10
  };
};

const EMPTY_TOUR = {
  photo: '',
  title: 'Journey',
  desc: '',
  address: '',
  city: '',
  distance: 0,
  maxGroupSize: 1,
  reviews: []
};

const TourDetails = ({ isDarkMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  // ── Find tour BEFORE any hooks that depend on it ──
  const tour = useMemo(() => {
    if (!tourData || !Array.isArray(tourData)) return null;
    return (
      tourData.find(
        (t) =>
          String(t.id) === String(id) ||
          String(t._id) === String(id)
      ) || null
    );
  }, [id]);

  const safeTour = tour || EMPTY_TOUR;

  // ── ALL hooks MUST be called unconditionally (Rules of Hooks) ──
  const [reviewText, setReviewText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [tourRating, setTourRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [localReviews, setLocalReviews] = useState(
    safeTour.reviews || []
  );
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Sync reviews when tour changes
  useEffect(() => {
    setLocalReviews(safeTour.reviews || []);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    const prevScheme = document.body.style.colorScheme;
    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.body.style.colorScheme = prevScheme;
    };
  }, [isDarkMode]);

  const { totalRating, avgRating } = useMemo(
    () => calculateAvgRating(localReviews),
    [localReviews]
  );

  const submitHandler = (e) => {
    e.preventDefault();
    if (!reviewText.trim() || tourRating === 0) return;

    const newReview = {
      name: reviewerName.trim() || 'Anonymous Traveler',
      rating: tourRating,
      comment: reviewText.trim(),
      date: new Date().toISOString()
    };

    setLocalReviews((prev) => [newReview, ...prev]);
    setReviewText('');
    setReviewerName('');
    setTourRating(0);
    setHoverRating(0);
    setSubmitSuccess(true);
    setTimeout(() => setSubmitSuccess(false), 3500);
  };

  const reveal = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: '0px 0px -10% 0px' },
          transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }
        };

  const heroReveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.98 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
      };

  const cssVars = {
    '--bg': isDarkMode ? '#061110' : '#F7FBFA',
    '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
    '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
    '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
    '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
    '--text': isDarkMode ? '#E9F6F3' : '#132E29',
    '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
    '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
    '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
    '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
    '--accent': isDarkMode ? '#F59E0B' : '#B45309',
    '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
    '--shadow': isDarkMode ? '0 18px 50px rgba(0,0,0,0.42)' : '0 18px 50px rgba(15,118,110,0.10)'
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

    .tour-details-root {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: var(--bg);
      color: var(--text);
      min-height: 100vh;
      -webkit-font-smoothing: antialiased;
    }
    .tour-details-root * { box-sizing: border-box; }
    .tour-details-root h1, .tour-details-root h2, .tour-details-root h3 {
      font-family: 'Sora', 'Inter', sans-serif; color: var(--text); margin: 0;
    }
    .tour-details-root p { color: var(--text-muted); }
    .tour-details-root :focus-visible {
      outline: 3px solid var(--focus); outline-offset: 3px; border-radius: 8px;
    }

    .td-container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
    .td-section { padding-block: clamp(3rem, 6vw, 5rem); }

    .back-btn {
      display: inline-flex; align-items: center; gap: 0.5rem;
      color: var(--text-muted); font-weight: 600; font-size: 0.95rem;
      text-decoration: none; margin-bottom: 2rem; transition: color 0.2s;
      background: none; border: none; cursor: pointer; padding: 0; font-family: inherit;
    }
    .back-btn:hover { color: var(--primary); }

    .hero-image-wrapper {
      width: 100%; aspect-ratio: 16 / 8; border-radius: 28px; overflow: hidden;
      border: 1px solid var(--border); box-shadow: var(--shadow); margin-bottom: 3rem;
      background: var(--bg-muted);
    }
    .hero-image-wrapper img {
      width: 100%; height: 100%; object-fit: cover; display: block;
    }

    .tour-header { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 2.5rem; }
    .tour-title {
      font-size: clamp(2rem, 4vw, 3rem); line-height: 1.1;
      letter-spacing: -0.03em; font-weight: 700;
    }
    .tour-meta { display: flex; flex-wrap: wrap; gap: 1.5rem; align-items: center; }
    .meta-item {
      display: flex; align-items: center; gap: 0.5rem;
      font-size: 0.95rem; font-weight: 500; color: var(--text-muted);
    }
    .meta-item svg { color: var(--primary); flex-shrink: 0; }

    .rating-badge {
      display: inline-flex; align-items: center; gap: 0.4rem;
      background: var(--surface); border: 1px solid var(--border-strong);
      padding: 0.4rem 0.85rem; border-radius: 999px; font-weight: 700; font-size: 0.9rem;
    }
    .rating-badge svg { color: #F59E0B; fill: #F59E0B; flex-shrink: 0; }

    .stats-grid {
      display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.25rem; margin-bottom: 3rem;
    }
    .stat-card {
      background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
      padding: 1.5rem; display: flex; flex-direction: column; gap: 0.5rem;
    }
    .stat-label {
      font-size: 0.8rem; font-weight: 600; color: var(--text-muted);
      text-transform: uppercase; letter-spacing: 0.06em;
    }
    .stat-value {
      font-size: 1.2rem; font-weight: 700; color: var(--text);
      font-family: 'Sora', sans-serif;
    }

    .content-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; }
    @media (min-width: 900px) { .content-grid { grid-template-columns: 1.5fr 1fr; } }

    .description-block h2 { font-size: 1.5rem; margin-bottom: 1rem; }
    .description-block p { font-size: 1.05rem; line-height: 1.75; margin-bottom: 1.5rem; }

    .booking-card {
      background: var(--surface); border: 1px solid var(--border-strong); border-radius: 24px;
      padding: 2rem; box-shadow: var(--shadow); height: fit-content;
    }
    @media (min-width: 900px) { .booking-card { position: sticky; top: 120px; } }
    .booking-card h3 { font-size: 1.25rem; margin-bottom: 0.5rem; }
    .booking-card p { font-size: 0.95rem; margin-bottom: 1.5rem; }

    .btn-primary {
      width: 100%; padding: 1rem; border-radius: 999px;
      background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
      color: #fff; font-size: 1rem; font-weight: 700; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center; gap: 0.6rem;
      box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
      transition: transform 0.25s ease, box-shadow 0.25s ease;
      text-decoration: none; font-family: inherit;
    }
    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
    }

    .reviews-section { margin-top: 4rem; }
    .reviews-section > h2 { font-size: 1.75rem; margin-bottom: 2rem; }

    .review-form {
      background: var(--surface); border: 1px solid var(--border); border-radius: 24px;
      padding: 2rem; margin-bottom: 2.5rem; box-shadow: var(--shadow);
    }
    .form-group { margin-bottom: 1.25rem; }
    .form-label {
      display: block; font-size: 0.875rem; font-weight: 600;
      margin-bottom: 0.5rem; color: var(--text);
    }
    .form-input, .form-textarea {
      width: 100%; padding: 0.875rem 1rem; border-radius: 14px;
      border: 1px solid var(--border-strong); background: var(--bg-muted);
      color: var(--text); font-size: 0.95rem; font-family: inherit; outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .form-input:focus, .form-textarea:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 4px var(--focus);
      background: var(--surface);
    }
    .form-textarea { resize: vertical; min-height: 100px; }

    .star-rating { display: flex; gap: 0.5rem; margin-bottom: 1.25rem; }
    .star-btn {
      background: none; border: none; cursor: pointer; padding: 0;
      color: var(--border-strong); transition: color 0.2s, transform 0.2s;
      line-height: 0;
    }
    .star-btn.active { color: #F59E0B; }
    .star-btn:hover { transform: scale(1.15); }

    .reviews-list { display: flex; flex-direction: column; gap: 1.25rem; }
    .review-card {
      background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
      padding: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem;
    }
    .review-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem; }
    .reviewer-name { font-weight: 700; font-size: 1rem; color: var(--text); }
    .review-date { font-size: 0.8rem; color: var(--text-muted); }
    .review-stars { display: flex; gap: 0.2rem; color: #F59E0B; }
    .review-comment { font-size: 0.95rem; line-height: 1.65; color: var(--text-muted); margin: 0; }

    .success-msg {
      padding: 1rem; border-radius: 14px; background: rgba(16, 185, 129, 0.08);
      border: 1px solid rgba(16, 185, 129, 0.25); color: #10b981; font-weight: 600;
      display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem; font-size: 0.9rem;
    }

    .not-found {
      min-height: 100vh; display: flex; align-items: center; justify-content: center;
      flex-direction: column; gap: 1.5rem; padding: 10rem 2rem 4rem; text-align: center;
    }
    .not-found h1 { font-size: 2rem; }
    .not-found .btn-primary { width: auto; padding: 0.9rem 1.8rem; }

    @media (max-width: 640px) { .td-container { width: calc(100% - 2rem); } }
  `;

  // ── NOT FOUND — rendered AFTER all hooks ──
  if (!tour) {
    return (
      <main className="tour-details-root" style={cssVars}>
        <style>{globalStyles}</style>
        <div className="not-found">
          <h1>Journey not found</h1>
          <p style={{ maxWidth: '40ch' }}>
            The tour you are looking for may have been removed or the link is incorrect.
          </p>
          <button onClick={() => navigate('/tours')} className="btn-primary">
            <ArrowLeft size={18} /> Browse all tours
          </button>
        </div>
      </main>
    );
  }

  const {
    photo = '',
    title = 'Journey',
    desc = '',
    address = '',
    city = '',
    distance = 0,
    maxGroupSize = 1
  } = tour;

  const bookingLink = `/booking?tour=${encodeURIComponent(title)}`;

  return (
    <main className="tour-details-root" style={cssVars}>
      <style>{globalStyles}</style>

      <section className="td-section" style={{ paddingTop: 'clamp(8rem, 12vw, 10rem)' }}>
        <div className="td-container">
          <button onClick={() => navigate('/tours')} className="back-btn">
            <ArrowLeft size={18} /> Back to all journeys
          </button>

          <motion.div className="hero-image-wrapper" {...heroReveal}>
            {photo ? (
              <img src={photo} alt={title} loading="eager" />
            ) : (
              <div style={{
                width: '100%', height: '100%', display: 'grid', placeItems: 'center',
                background: 'var(--bg-muted)', color: 'var(--text-muted)', fontSize: '1rem'
              }}>
                Image unavailable
              </div>
            )}
          </motion.div>

          <div className="tour-header">
            <h1 className="tour-title">{title}</h1>
            <div className="tour-meta">
              <div className="rating-badge">
                <Star size={16} />
                {avgRating === 0 ? 'New' : avgRating.toFixed(1)}
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                  ({totalRating} {totalRating === 1 ? 'review' : 'reviews'})
                </span>
              </div>
              {(address || city) && (
                <div className="meta-item">
                  <MapPin size={16} /> {[address, city].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Location</span>
              <span className="stat-value">{city || 'Various'}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Distance</span>
              <span className="stat-value">{distance ? `${distance} km` : 'On request'}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Group Size</span>
              <span className="stat-value">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Users size={16} style={{ color: 'var(--primary)' }} />
                  Max {maxGroupSize}
                </span>
              </span>
            </div>
          </div>

          <div className="content-grid">
            <motion.div className="description-block" {...reveal()}>
              <h2>About this journey</h2>
              <p>{desc || 'Details for this journey are being prepared. Contact us for more information.'}</p>
              <p>
                 We coordinate logistics,
                accommodation, and experiences to ensure your trip is seamless from departure to return.
              </p>
            </motion.div>

            <motion.div className="booking-card" {...reveal(0.1)}>
              <h3>Ready to explore?</h3>
              <p>Secure your spot or request a customized itinerary for your group.</p>
              <Link to={bookingLink} className="btn-primary">
                Reserve this trip <ChevronRight size={18} />
              </Link>
            </motion.div>
          </div>

          {/* REVIEWS */}
          <motion.div className="reviews-section" {...reveal(0.05)}>
            <h2>Traveler Reviews</h2>

            <form onSubmit={submitHandler} className="review-form">
              {submitSuccess && (
                <div className="success-msg" role="status">
                  <CheckCircle size={18} /> Review submitted successfully!
                </div>
              )}

              <div className="form-group">
                <span className="form-label">Your Rating</span>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star-btn ${(hoverRating || tourRating) >= star ? 'active' : ''}`}
                      onClick={() => setTourRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      <Star
                        size={28}
                        fill={(hoverRating || tourRating) >= star ? 'currentColor' : 'none'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reviewerName" className="form-label">Name</label>
                <input
                  id="reviewerName"
                  type="text"
                  className="form-input"
                  placeholder="Your name (optional)"
                  value={reviewerName}
                  onChange={(e) => setReviewerName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="reviewText" className="form-label">Your Experience</label>
                <textarea
                  id="reviewText"
                  className="form-textarea"
                  placeholder="Share your thoughts about this journey..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn-primary" style={{ maxWidth: '240px' }}>
                <Send size={18} /> Submit Review
              </button>
            </form>

            <div className="reviews-list">
              {localReviews.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem 0' }}>
                  Be the first to review this journey.
                </p>
              ) : (
                localReviews.map((review, index) => (
                  <motion.div
                    key={`${review.name}-${index}`}
                    className="review-card"
                    initial={reduceMotion ? undefined : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                  >
                    <div className="review-header">
                      <span className="reviewer-name">{review.name || 'Traveler'}</span>
                      <span className="review-date">
                        {review.date
                          ? new Date(review.date).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })
                          : 'Recently'}
                      </span>
                    </div>
                    <div className="review-stars">
                      {[...Array(Math.min(Number(review.rating) || 5, 5))].map((_, i) => (
                        <Star key={i} size={16} fill="currentColor" />
                      ))}
                    </div>
                    <p className="review-comment">{review.comment || 'Amazing tour!'}</p>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TourDetails;



























// import React, { useRef, useState, useEffect } from "react";
// import { useParams, useNavigate, Link } from "react-router-dom";
// import { motion, useReducedMotion } from "framer-motion";
// import { 
//   Star, MapPin, Users, Ruler, ArrowLeft, Send, CheckCircle, 
//   Calendar, MessageSquare, ChevronRight 
// } from "lucide-react";
// import tourData from "../assets/data/tours"; 
// import calculateAvgRating from "./../utils/avgRating";

// const TourDetails = ({ isDarkMode = false }) => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const reduceMotion = useReducedMotion();
  
//   const tour = tourData.find((t) => t.id === id || t._id === id);

//   const [reviewText, setReviewText] = useState('');
//   const [reviewerName, setReviewerName] = useState('');
//   const [tourRating, setTourRating] = useState(0);
//   const [hoverRating, setHoverRating] = useState(0);
//   const [localReviews, setLocalReviews] = useState(tour?.reviews || []);
//   const [submitSuccess, setSubmitSuccess] = useState(false);

//   useEffect(() => {
//     document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
//     document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';
//     return () => {
//       document.body.style.backgroundColor = '';
//       document.body.style.colorScheme = '';
//     };
//   }, [isDarkMode]);

//   if (!tour) {
//     return (
//       <main className="tour-details-root" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1.5rem', paddingTop: '10rem' }}>
//         <h1 style={{ fontSize: '2rem', fontWeight: 700 }}>Journey not found</h1>
//         <button onClick={() => navigate('/tours')} className="btn-primary">
//           <ArrowLeft size={18} /> Browse all tours
//         </button>
//       </main>
//     );
//   }

//   const { photo, title, desc, address, city, distance, maxGroupSize } = tour;
//   const { totalRating, avgRating } = calculateAvgRating(localReviews);

//   const submitHandler = (e) => {
//     e.preventDefault();
//     if (!reviewText.trim() || tourRating === 0) return;
    
//     const newReview = {
//       name: reviewerName || 'Anonymous Traveler',
//       rating: tourRating,
//       comment: reviewText,
//       date: new Date().toISOString()
//     };
    
//     setLocalReviews([newReview, ...localReviews]);
//     setReviewText('');
//     setReviewerName('');
//     setTourRating(0);
//     setSubmitSuccess(true);
//     setTimeout(() => setSubmitSuccess(false), 3000);
//   };

//   const reveal = (delay = 0) => reduceMotion ? {} : {
//     initial: { opacity: 0, y: 22 },
//     whileInView: { opacity: 1, y: 0 },
//     viewport: { once: true, margin: '0px 0px -10% 0px' },
//     transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }
//   };

//   const globalStyles = `
//     @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

//     .tour-details-root {
//       font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
//       background: var(--bg);
//       color: var(--text);
//       min-height: 100vh;
//       -webkit-font-smoothing: antialiased;
//     }
//     .tour-details-root * { box-sizing: border-box; }
//     .tour-details-root h1, .tour-details-root h2, .tour-details-root h3 { font-family: 'Sora', sans-serif; color: var(--text); }
//     .tour-details-root p { color: var(--text-muted); }

//     .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
//     .section { padding-block: clamp(3rem, 6vw, 5rem); }
//     .sectiona{ margin: 4rem}

//     .back-btn {
//       display: inline-flex; align-items: center; gap: 0.5rem;
//       color: var(--text-muted); font-weight: 600; font-size: 0.95rem;
//       text-decoration: none; margin-bottom: 2rem; transition: color 0.2s;
//       background: none; border: none; cursor: pointer; padding: 0;
//     }
//     .back-btn:hover { color: var(--primary); }

//     .hero-image-wrapper {
//       width: 100%; aspect-ratio: 16 / 8; border-radius: 28px; overflow: hidden;
//       border: 1px solid var(--border); box-shadow: var(--shadow); margin-bottom: 3rem;
//     }
//     .hero-image-wrapper img { width: 100%; height: 100%; object-fit: cover; display: block; }

//     .tour-header { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 2.5rem; }
    
//     .tour-title { font-size: clamp(2rem, 4vw, 3rem); line-height: 1.1; letter-spacing: -0.03em; margin: 0; font-weight: 700; }
    
//     .tour-meta { display: flex; flex-wrap: wrap; gap: 1.5rem; align-items: center; }
//     .meta-item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.95rem; font-weight: 500; color: var(--text-muted); }
//     .meta-item svg { color: var(--primary); }

//     .rating-badge {
//       display: inline-flex; align-items: center; gap: 0.4rem;
//       background: var(--surface); border: 1px solid var(--border-strong);
//       padding: 0.4rem 0.85rem; border-radius: 999px; font-weight: 700; font-size: 0.9rem;
//     }
//     .rating-badge svg { color: #F59E0B; fill: #F59E0B; }

//     .stats-grid {
//       display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//       gap: 1.25rem; margin-bottom: 3rem;
//     }
//     .stat-card {
//       background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
//       padding: 1.5rem; display: flex; flex-direction: column; gap: 0.5rem;
//     }
//     .stat-label { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
//     .stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text); font-family: 'Sora', sans-serif; }

//     .content-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; }
//     @media (min-width: 900px) { .content-grid { grid-template-columns: 1.5fr 1fr; } }

//     .description-block h2 { font-size: 1.5rem; margin-bottom: 1rem; }
//     .description-block p { font-size: 1.05rem; line-height: 1.75; margin-bottom: 1.5rem; }

//     .booking-card {
//       background: var(--surface); border: 1px solid var(--border-strong); border-radius: 24px;
//       padding: 2rem; box-shadow: var(--shadow); position: sticky; top: 120px; height: fit-content;
//     }
//     .booking-card h3 { font-size: 1.25rem; margin-bottom: 0.5rem; }
//     .booking-card p { font-size: 0.95rem; margin-bottom: 1.5rem; }

//     .btn-primary {
//       width: 100%; padding: 1rem; border-radius: 999px;
//       background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
//       color: #fff; font-size: 1rem; font-weight: 700; border: none; cursor: pointer;
//       display: flex; align-items: center; justify-content: center; gap: 0.6rem;
//       box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28); transition: all 0.25s ease;
//       text-decoration: none;
//     }
//     .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36); }

//     .reviews-section { margin-top: 4rem; }
//     .reviews-section h2 { font-size: 1.75rem; margin-bottom: 2rem; }

//     .review-form {
//       background: var(--surface); border: 1px solid var(--border); border-radius: 24px;
//       padding: 2rem; margin-bottom: 2.5rem; box-shadow: var(--shadow);
//     }
//     .form-group { margin-bottom: 1.25rem; }
//     .form-label { display: block; font-size: 0.875rem; font-weight: 600; margin-bottom: 0.5rem; color: var(--text); }
//     .form-input, .form-textarea {
//       width: 100%; padding: 0.875rem 1rem; border-radius: 14px;
//       border: 1px solid var(--border-strong); background: var(--bg-muted);
//       color: var(--text); font-size: 0.95rem; font-family: inherit; outline: none;
//       transition: all 0.2s ease;
//     }
//     .form-input:focus, .form-textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 4px var(--focus); background: var(--surface); }
//     .form-textarea { resize: vertical; min-height: 100px; }

//     .star-rating { display: flex; gap: 0.5rem; margin-bottom: 1.25rem; }
//     .star-btn { background: none; border: none; cursor: pointer; padding: 0; color: var(--border-strong); transition: color 0.2s, transform 0.2s; }
//     .star-btn.active { color: #F59E0B; }
//     .star-btn:hover { transform: scale(1.15); }

//     .reviews-list { display: flex; flex-direction: column; gap: 1.25rem; }
//     .review-card {
//       background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
//       padding: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem;
//     }
//     .review-header { display: flex; justify-content: space-between; align-items: center; }
//     .reviewer-name { font-weight: 700; font-size: 1rem; color: var(--text); }
//     .review-date { font-size: 0.8rem; color: var(--text-muted); }
//     .review-stars { display: flex; gap: 0.2rem; color: #F59E0B; }
//     .review-comment { font-size: 0.95rem; line-height: 1.65; color: var(--text-muted); margin: 0; }

//     .success-msg {
//       padding: 1rem; border-radius: 14px; background: rgba(16, 185, 129, 0.08);
//       border: 1px solid rgba(16, 185, 129, 0.25); color: #10b981; font-weight: 600;
//       display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem; font-size: 0.9rem;
//     }

//     @media (max-width: 640px) { .container { width: calc(100% - 2rem); } }
//   `;

//   return (
//     <main
//       className="tour-details-root"
//       style={{
//         '--bg': isDarkMode ? '#061110' : '#F7FBFA',
//         '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
//         '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
//         '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
//         '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
//         '--text': isDarkMode ? '#E9F6F3' : '#132E29',
//         '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
//         '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
//         '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
//         '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
//         '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
//         '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
//       }}
//     >
//       <style>{globalStyles}</style>

//       <section className="section" style={{ paddingTop: 'clamp(8rem, 12vw, 10rem)' }}>
//         <div className="container">
//           <button onClick={() => navigate('/tours')} className="back-btn">
//             <ArrowLeft size={18} /> Back to all journeys
//           </button>

//           <motion.div 
//             className="hero-image-wrapper"
//             initial={{ opacity: 0, scale: 0.98 }}
//             animate={{ opacity: 1, scale: 1 }}
//             transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
//           >
//             <img src={photo} alt={title} />
//           </motion.div>

//           <div className="tour-header">
//             <h1 className="tour-title">{title}</h1>
//             <div className="tour-meta">
//               <div className="rating-badge">
//                 <Star size={16} />
//                 {avgRating === 0 ? 'New' : avgRating.toFixed(1)}
//                 <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
//                   ({totalRating} {totalRating === 1 ? 'review' : 'reviews'})
//                 </span>
//               </div>
//               <div className="meta-item">
//                 <MapPin size={16} /> {address}, {city}
//               </div>
//             </div>
//           </div>

//           <div className="stats-grid">
//             <div className="stat-card">
//               <span className="stat-label">Location</span>
//               <span className="stat-value">{city}</span>
//             </div>
//             <div className="stat-card">
//               <span className="stat-label">Distance</span>
//               <span className="stat-value">{distance} km</span>
//             </div>
//             <div className="stat-card">
//               <span className="stat-label">Group Size</span>
//               <span className="stat-value">Max {maxGroupSize} people</span>
//             </div>
//           </div>

//           <div className="content-grid">
//             <div className="description-block">
//               <h2>About this journey</h2>
//               <p>{desc}</p>
//               <p>
//                 Oscar Global is an independent travel consultancy. We coordinate logistics, 
//                 accommodation, and experiences to ensure your trip is seamless from departure to return.
//               </p>
//             </div>

//             <div className="booking-card">
//               <h3>Ready to explore?</h3>
//               <p>Secure your spot or request a customized itinerary for your group.</p>
//               <Link to={`/booking?tour=${title}`} className="btn-primary">
//                 Reserve this trip <ChevronRight size={18} />
//               </Link>
//             </div>
//           </div>

//           <div className="reviews-section">
//             <h2>Traveler Reviews</h2>
            
//             <form onSubmit={submitHandler} className="review-form">
//               {submitSuccess && (
//                 <div className="success-msg">
//                   <CheckCircle size={18} /> Review submitted successfully!
//                 </div>
//               )}
              
//               <div className="form-group">
//                 <label className="form-label">Your Rating</label>
//                 <div className="star-rating">
//                   {[1, 2, 3, 4, 5].map((star) => (
//                     <button
//                       key={star}
//                       type="button"
//                       className={`star-btn ${(hoverRating || tourRating) >= star ? 'active' : ''}`}
//                       onClick={() => setTourRating(star)}
//                       onMouseEnter={() => setHoverRating(star)}
//                       onMouseLeave={() => setHoverRating(0)}
//                       aria-label={`Rate ${star} stars`}
//                     >
//                       <Star size={28} fill={(hoverRating || tourRating) >= star ? 'currentColor' : 'none'} />
//                     </button>
//                   ))}
//                 </div>
//               </div>

//               <div className="form-group">
//                 <label htmlFor="reviewerName" className="form-label">Name</label>
//                 <input
//                   id="reviewerName"
//                   type="text"
//                   className="form-input"
//                   placeholder="Your name (optional)"
//                   value={reviewerName}
//                   onChange={(e) => setReviewerName(e.target.value)}
//                 />
//               </div>

//               <div className="form-group">
//                 <label htmlFor="reviewText" className="form-label">Your Experience</label>
//                 <textarea
//                   id="reviewText"
//                   className="form-textarea"
//                   placeholder="Share your thoughts about this journey..."
//                   value={reviewText}
//                   onChange={(e) => setReviewText(e.target.value)}
//                   required
//                 />
//               </div>

//               <button type="submit" className="btn-primary" style={{ maxWidth: '240px' }}>
//                 <Send size={18} /> Submit Review
//               </button>
//             </form>

//             <div className="reviews-list">
//               {localReviews.length === 0 ? (
//                 <p style={{ textAlign: 'center', padding: '2rem' }}>Be the first to review this journey.</p>
//               ) : (
//                 localReviews.map((review, index) => (
//                   <motion.div 
//                     key={index} 
//                     className="review-card"
//                     initial={{ opacity: 0, y: 10 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ delay: index * 0.05 }}
//                   >
//                     <div className="review-header">
//                       <span className="reviewer-name">{review.name}</span>
//                       <span className="review-date">
//                         {review.date ? new Date(review.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently'}
//                       </span>
//                     </div>
//                     <div className="review-stars">
//                       {[...Array(review.rating || 5)].map((_, i) => (
//                         <Star key={i} size={16} fill="currentColor" />
//                       ))}
//                     </div>
//                     <p className="review-comment">{review.comment || "Amazing tour!"}</p>
//                   </motion.div>
//                 ))
//               )}
//             </div>
//           </div>
//         </div>
//       </section>
//     </main>
//   );
// };

// export default TourDetails;





















// import React, { useRef, useState } from "react";
// import "../styles/tour-details.css";
// import { Container, Row, Col, Form } from "reactstrap";
// import { useParams } from "react-router-dom";
// import tourData from "../assets/data/tours"; // Make sure this imports your local data correctly
// import calculateAvgRating from "./../utils/avgRating";
// // import avatar from "../assets/images/avatar.svg";
// // import Booking from "../components/Booking/Booking";
// import Newsletter from "../shared/Newsletter";

// const TourDetails = () => {
//   const { id } = useParams();
//   const reviewMsgRef = useRef("");
//   const [ setTourRating] = useState(null);

//   // Check that the tourData objects use the correct identifier; if it's _id, change this line:
//   const tour = tourData.find((tour) => tour.id === id); // Change 'id' to '_id' if necessary

//   if (!tour) {
//     return <h2>Tour not found</h2>;
//   }

//   const {
//     photo,
//     title,
//     desc,
//     address,
//     reviews,
//     city,
//     distance,
//     maxGroupSize,
//   } = tour;

//   const { totalRating, avgRating } = calculateAvgRating(reviews);

//   // const options = { day: "numeric", month: "long", year: "numeric" };

//   const submitHandler = (e) => {
//     e.preventDefault();
//     // const reviewText = reviewMsgRef.current.value;
//     // Submit to API logic goes here
//   };

//   // function getRandomDate(start = new Date(2010, 0, 1), end = new Date()) {
//   //   const date = new Date(
//   //     start.getTime() + Math.random() * (end.getTime() - start.getTime())
//   //   );
//   //   return date.toISOString(); // returns format like "2015-04-06T12:00:00Z"
//   // }

//   return (
//     <>
//       <section>
//         <Container>
//           <Row>
//             <Col lg="12">
//               {/* later change to lg='8', when you activate booking */}
//               <div className="tour__content">
//                 <img src={photo} alt={title} />

//                 <div className="tour__info">
//                   <h2>{title}</h2>
//                   <div className="d-flex align-items-center gap-5">
//                     <span className="tour__rating d-flex align-items-center gap-1">
//                       <i
//                         className="ri-star-fill"
//                         style={{ color: "var(--secondary-color)" }}
//                       ></i>{" "}
//                       {avgRating === 0 ? null : avgRating}
//                       {totalRating === 0 ? (
//                         "Not rated"
//                       ) : (
//                         <span>({reviews.length})</span>
//                       )}
//                     </span>

//                     <span>
//                       <i className="ri-map-pin-user-fill"></i> {address}
//                     </span>
//                   </div>
//                   <div className="tour__extra-details">
//                     <span>
//                       <i className="ri-map-pin-2-line"></i>
//                       {city}
//                     </span>
//                     {/* <span>
//                       <i className="ri-money-dollar-circle-line"></i>${price} /per
//                       person
//                     </span> */}
//                     <span>
//                       <i className="ri-map-pin-time-line"></i>
//                       {distance} k/m
//                     </span>
//                     <span>
//                       <i className="ri-group-line"></i>
//                       {maxGroupSize} people
//                     </span>
//                   </div>
//                   <h5>Description</h5>
//                   <p>{desc}</p>
//                 </div>

//                 {/* ============ tour reviews section start ============ */}
//                 <div className="tour__reviews mt-4">
//                   <h4>Reviews ({reviews?.length} reviews)</h4>

//                   <Form onSubmit={submitHandler}>
//                     <div className="d-flex align-items-center gap-3 mb-4 rating__group">
//                       <span onClick={() => setTourRating(1)}>
//                         1 <i className="ri-star-s-fill"></i>
//                       </span>
//                       <span onClick={() => setTourRating(2)}>
//                         2 <i className="ri-star-s-fill"></i>
//                       </span>
//                       <span onClick={() => setTourRating(3)}>
//                         3 <i className="ri-star-s-fill"></i>
//                       </span>
//                       <span onClick={() => setTourRating(4)}>
//                         4 <i className="ri-star-s-fill"></i>
//                       </span>
//                       <span onClick={() => setTourRating(5)}>
//                         5 <i className="ri-star-s-fill"></i>
//                       </span>
//                     </div>

//                     <div className="review__input">
//                       <input
//                         type="text"
//                         ref={reviewMsgRef}
//                         placeholder="share your thoughts"
//                         required
//                       />
//                       <button
//                         className="btn primary__btn text-white"
//                         type="submit"
//                       >
//                         Submit
//                       </button>
//                     </div>
//                   </Form>

//                   {/* <ListGroup className="user__reviews">
//                     {reviews?.map((review, index) => (
//                       <div key={index} className="review__item">
//                         <img src={review.avatar} alt="" />
//                         <div className="w-100 d-flex justify-content-between">
//                           <div className="align-items-center justify-content-between">
//                             <h5>{review.name}</h5>
//                             <p>
//                               {new Date(
//                                 review.date || getRandomDate()
//                               ).toLocaleDateString("en-US", options)}
//                             </p>

//                             <h6>{review.comment || "Amazing tour"}</h6>
//                           </div>
//                           <span className="d-flex align-items-center">
//                             {review.rating || 5}
//                             <i className="ri-star-s-fill"></i>
//                           </span>
//                         </div>
//                       </div>
//                     ))}
//                   </ListGroup> */}
//                 </div>
//                 {/* ============ tour reviews section end ============ */}
//               </div>
//             </Col>
//             {/* <Col lg="4">
//               <Booking tour={tour} avgRating={avgRating} />
//             </Col> */}
//           </Row>
//         </Container>
//       </section>
//       <Newsletter />
//     </>
//   );
// };

// export default TourDetails;

// // import React, { useRef, useState } from "react";
// // import "../styles/tour-details.css";
// // import { Container, Row, Col, Form, ListGroup } from "reactstrap";
// // import { useParams } from "react-router-dom";
// // import tourData from "../assets/data/tours";
// // import calculateAvgRating from "./../utils/avgRating";
// // import avatar from "../assets/images/avatar.svg";
// // import Booking from "../components/Booking/Booking";
// // import Newsletter from "../shared/Newsletter";

// // const TourDetails = () => {
// //   const { id } = useParams();
// //   const reviewMsgRef = useRef("");
// //   const [tourRating, setTourRating] = useState(null);

// //   // This is a static data, later we will call our API and load our data from database
// //   const tour = tourData.find((tour) => tour.id === Number(id)); // Convert id to number

// //   if (!tour) {
// //     return <h2>Tour not found</h2>; // Handle invalid tour ID
// //   }

// //   // Destructure properties
// //   const {
// //     photo,
// //     title,
// //     desc,
// //     price,
// //     address,
// //     reviews,
// //     city,
// //     distance,
// //     maxGroupSize,
// //   } = tour;

// //   const { totalRating, avgRating } = calculateAvgRating(reviews);

// //   //format date
// //   const options = { day: "numeric", month: "long", year: "numeric" };

// //   //submit request to the server
// //   const submitHandler = (e) => {
// //     e.preventDefault();
// //     const reviewText = reviewMsgRef.current.value;

// //     //later we will call our api
// //   };

// //   return (
// //     <>
// //       <section>
// //         <Container>
// //           <Row>
// //             <Col lg="8">
// //               <div className="tour__content">
// //                 <img src={photo} alt={title} />

// //                 <div className="tour__info">
// //                   <h2>{title}</h2>
// //                   <div className="d-flex align-items-center gap-5">
// //                     <span className="tour__rating d-flex align-items-center gap-1">
// //                       <i
// //                         class="ri-star-fill"
// //                         style={{ color: "var(--secondary-color)" }}
// //                       ></i>{" "}
// //                       {avgRating === 0 ? null : avgRating}
// //                       {totalRating === 0 ? (
// //                         "Not rated"
// //                       ) : (
// //                         <span>({reviews.length})</span>
// //                       )}
// //                     </span>

// //                     <span>
// //                       <i class="ri-map-pin-user-fill"></i> {address}
// //                     </span>
// //                   </div>
// //                   <div className="tour__extra-details">
// //                     <span>
// //                       <i class="ri-map-pin-2-line"></i>
// //                       {city}
// //                     </span>
// //                     <span>
// //                       <i class="ri-money-dollar-circle-line"></i>${price} /per
// //                       person
// //                     </span>
// //                     <span>
// //                       <i class="ri-map-pin-time-line"></i>
// //                       {distance} k/m
// //                     </span>
// //                     <span>
// //                       <i class="ri-group-line"></i>
// //                       {maxGroupSize} people
// //                     </span>
// //                   </div>
// //                   <h5>Description</h5>
// //                   <p>{desc}</p>
// //                 </div>

// //                 {/* ============ tour reviews section start ============ */}
// //                 <div className="tour__reviews mt-4">
// //                   <h4>Reviews ({reviews?.length} reviews)</h4>

// //                   <Form onSubmit={submitHandler}>
// //                     <div className="d-flex align-items-center gap-3 mb-4 rating__group">
// //                       <span onClick={() => setTourRating(1)}>
// //                         1 <i class="ri-star-s-fill"></i>
// //                       </span>
// //                       <span onClick={() => setTourRating(2)}>
// //                         2 <i class="ri-star-s-fill"></i>
// //                       </span>
// //                       <span onClick={() => setTourRating(3)}>
// //                         3 <i class="ri-star-s-fill"></i>
// //                       </span>
// //                       <span onClick={() => setTourRating(4)}>
// //                         4 <i class="ri-star-s-fill"></i>
// //                       </span>
// //                       <span onClick={() => setTourRating(5)}>
// //                         5 <i class="ri-star-s-fill"></i>
// //                       </span>
// //                     </div>

// //                     <div className="review__input">
// //                       <input
// //                         type="text"
// //                         ref={reviewMsgRef}
// //                         placeholder="share your thoughts"
// //                         required
// //                       />
// //                       <button
// //                         className="btn primary__btn text-white"
// //                         type="submit"
// //                       >
// //                         Submit
// //                       </button>
// //                     </div>
// //                   </Form>

// //                   <ListGroup className="user__reviews">
// //                     {reviews?.map((review) => (
// //                       <div className="review__item">
// //                         <img src={avatar} alt="" srcset="" />

// //                         <div className="w-100 d-flex justify-content-between">
// //                           <div className=" align-items-center justify-content-between">
// //                             <h5>Ituma</h5>
// //                             <p>
// //                               {new Date("04-06-2015").toLocaleDateString(
// //                                 "en-US",
// //                                 options
// //                               )}
// //                             </p>
// //                             <h6>Amazing tour</h6>
// //                           </div>
// //                           <span className="d-flex align-items-center">
// //                             5<i class="ri-star-s-fill"></i>
// //                           </span>
// //                         </div>
// //                       </div>
// //                     ))}
// //                   </ListGroup>
// //                 </div>
// //                 {/* ============ tour reviews section end ============ */}
// //               </div>
// //             </Col>
// //             <Col lg="4">
// //               <Booking tour={tour} avgRating={avgRating} />
// //             </Col>
// //           </Row>
// //         </Container>
// //       </section>
// //       <Newsletter />
// //     </>
// //   );
// // };

// // export default TourDetails;
